<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="flex flex-col modal-container">
      <woot-modal-header
        :header-title="$t('CRM_CONFIGS.LINK.TITLE')"
        header-content="Tìm kiếm và chọn khách hàng từ hệ thống CRM để liên kết với liên hệ này."
      />

      <div class="px-8 pb-8 pt-4 overflow-y-auto max-h-[85vh]">
        <!-- Search Input Section -->
        <div class="flex flex-col mb-4">
          <label
            class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2"
          >
            {{ $t('CRM_CONFIGS.LINK.PHONE_LABEL') }}
          </label>
          <div class="flex items-center gap-2">
            <div class="flex-1">
              <input
                v-model.trim="phoneSearch"
                type="text"
                class="m-0 h-10 w-full"
                :placeholder="$t('CRM_CONFIGS.LINK.PHONE_PLACEHOLDER')"
                @keyup.enter="searchCrm"
              />
            </div>
            <woot-button
              :is-loading="isSearching"
              :disabled="!phoneSearch"
              size="medium"
              class="h-10"
              @click="searchCrm"
            >
              {{ $t('CRM_CONFIGS.LINK.SEARCH_BTN') }}
            </woot-button>
          </div>
        </div>

        <transition name="fade" mode="out-in">
          <div v-if="searchCompleted" :key="searchCompleted">
            <!-- Results List -->
            <div v-if="searchResults && searchResults.length > 0">
              <h4
                class="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-4 mt-6"
              >
                {{ $t('CRM_CONFIGS.LINK.RESULTS_TITLE') }}
              </h4>
              <div class="flex flex-col gap-3">
                <div
                  v-for="guest in searchResults"
                  :key="guest.id"
                  class="flex items-center justify-between p-4 rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-25/50 dark:bg-slate-800/30 hover:shadow-sm transition-all"
                >
                  <div class="flex items-center gap-4">
                    <div
                      class="w-10 h-10 rounded-full bg-woot-50 dark:bg-woot-900/40 flex items-center justify-center text-woot-600 dark:text-woot-400 font-bold text-sm border border-woot-100 dark:border-woot-900/50"
                    >
                      {{ (guest.hoTen || '?').charAt(0).toUpperCase() }}
                    </div>
                    <div class="flex flex-col min-w-0">
                      <div
                        class="font-bold text-slate-800 dark:text-white leading-tight mb-0.5 truncate"
                      >
                        {{ guest.hoTen || 'Không tên' }}
                      </div>
                      <div
                        class="text-[11px] text-slate-500 dark:text-slate-400 font-medium tracking-tight truncate"
                      >
                        {{ guest.dienThoai }}
                        <span class="mx-1 opacity-20">•</span>
                        {{ guest.maKhach }}
                      </div>
                    </div>
                  </div>
                  <woot-button
                    size="small"
                    variant="smooth"
                    color-scheme="primary"
                    icon="link"
                    :is-loading="isLinking && selectedGuestId === guest.id"
                    @click="linkContact(guest)"
                  >
                    {{ $t('CRM_CONFIGS.LINK.SELECT_LINK_BTN') }}
                  </woot-button>
                </div>
              </div>
            </div>

            <!-- Create Section (No results) -->
            <div v-else class="animate-in fade-in slide-in-from-top-2">
              <div
                class="mb-6 flex flex-col items-center justify-center text-center py-6 bg-slate-50/50 dark:bg-slate-800/10 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800"
              >
                <div
                  class="w-12 h-12 bg-white dark:bg-slate-800 rounded-full shadow-sm flex items-center justify-center text-slate-300 dark:text-slate-600 mb-3 border border-slate-100 dark:border-slate-800"
                >
                  <fluent-icon icon="person-search" size="24" />
                </div>
                <h4
                  class="text-sm font-bold text-slate-800 dark:text-slate-100 mb-1"
                >
                  {{ $t('CRM_CONFIGS.CREATE_GUEST.TITLE') }}
                </h4>
                <p
                  class="text-xs text-slate-500 dark:text-slate-400 max-w-[280px] m-0 leading-normal"
                >
                  {{ $t('CRM_CONFIGS.CREATE_GUEST.DESC') }}
                </p>
              </div>

              <form class="mt-4" @submit.prevent="submitCreateGuest">
                <div class="grid grid-cols-2 gap-x-6 gap-y-1">
                  <woot-input
                    v-model.trim="newGuest.dienThoai"
                    :label="$t('CRM_CONFIGS.CREATE_GUEST.FORM.PHONE_LABEL')"
                    class="mb-2"
                  />
                  <woot-input
                    v-model.trim="newGuest.hoTen"
                    :label="$t('CRM_CONFIGS.CREATE_GUEST.FORM.NAME_LABEL')"
                    :placeholder="
                      $t('CRM_CONFIGS.CREATE_GUEST.FORM.NAME_PLACEHOLDER')
                    "
                    :error="
                      $v.newGuest.hoTen.$error
                        ? $t('CRM_CONFIGS.CREATE_GUEST.FORM.NAME_ERROR')
                        : ''
                    "
                    class="mb-2"
                    @blur="$v.newGuest.hoTen.$touch"
                  />

                  <woot-input
                    v-model="newGuest.birthday"
                    :label="$t('CRM_CONFIGS.CREATE_GUEST.FORM.DOB_LABEL')"
                    type="date"
                    class="mb-2"
                  />

                  <div class="mb-2 text-left">
                    <label
                      class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2"
                    >
                      {{ $t('CRM_CONFIGS.CREATE_GUEST.FORM.GENDER_LABEL') }}
                    </label>
                    <select
                      v-model="newGuest.gioiTinh"
                      class="w-full h-[40px] text-sm bg-slate-50 dark:bg-slate-800/40 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700/80 rounded-lg px-3 outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500 transition-all shadow-sm"
                    >
                      <option value="">-- Chọn giới tính --</option>
                      <option value="Nam">Nam</option>
                      <option value="Nữ">Nữ</option>
                      <option value="Khác">Khác</option>
                    </select>
                  </div>

                  <woot-input
                    v-model.trim="newGuest.quocTich"
                    :label="$t('CRM_CONFIGS.CREATE_GUEST.FORM.NATION_LABEL')"
                    placeholder="VD: VN"
                    class="mb-2"
                  />
                  <woot-input
                    v-model.trim="newGuest.email"
                    :label="$t('CRM_CONFIGS.CREATE_GUEST.FORM.EMAIL_LABEL')"
                    type="email"
                    class="mb-2"
                  />

                  <div class="col-span-2">
                    <woot-input
                      v-model.trim="newGuest.diaChi"
                      :label="$t('CRM_CONFIGS.CREATE_GUEST.FORM.ADDRESS_LABEL')"
                      class="mb-2"
                    />
                  </div>
                </div>

                <div
                  class="flex justify-end gap-3 pt-6 mt-2 border-t border-slate-50 dark:border-slate-800/50"
                >
                  <woot-button
                    variant="clear"
                    color-scheme="secondary"
                    @click.prevent="onClose"
                  >
                    {{ $t('CRM_CONFIGS.CREATE_GUEST.FORM.CANCEL_BTN') }}
                  </woot-button>
                  <woot-button
                    type="submit"
                    icon="checkmark"
                    :is-loading="isCreating"
                    :disabled="$v.newGuest.$invalid"
                  >
                    {{ $t('CRM_CONFIGS.CREATE_GUEST.FORM.SUBMIT_BTN') }}
                  </woot-button>
                </div>
              </form>
            </div>
          </div>
        </transition>

        <!-- Footer for search state -->
        <div
          v-if="!searchCompleted || (searchResults && searchResults.length > 0)"
          class="flex justify-end border-t border-slate-50 dark:border-slate-800/50 pt-4 mt-6"
        >
          <woot-button
            variant="clear"
            color-scheme="secondary"
            @click.prevent="onClose"
          >
            {{ $t('CRM_CONFIGS.LINK.CANCEL_BTN') }}
          </woot-button>
        </div>
      </div>
    </div>
  </woot-modal>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';

export default {
  mixins: [alertMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    contact: {
      type: Object,
      required: true,
    },
    onClose: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      phoneSearch: '',
      isSearching: false,
      searchCompleted: false,
      searchResults: [],
      isLinking: false,
      isCreating: false,
      selectedGuestId: null,
      newGuest: {
        hoTen: '',
        gioiTinh: '',
        birthday: '',
        quocTich: '',
        diaChi: '',
        dienThoai: '',
        email: '',
      },
    };
  },
  validations: {
    newGuest: {
      hoTen: { required },
    },
  },
  mounted() {
    if (this.contact && this.contact.phone_number) {
      this.phoneSearch = String(this.contact.phone_number).replace(/^\+/, '');
    }
    if (this.contact && this.contact.name) {
      this.newGuest.hoTen = this.contact.name;
    }
    if (this.contact && this.contact.email) {
      this.newGuest.email = this.contact.email;
    }
  },
  methods: {
    async searchCrm() {
      if (!this.phoneSearch) return;

      this.isSearching = true;
      this.searchCompleted = false;
      this.searchResults = [];

      // Update the newGuest's dienThoai to match the searched phone number when searching
      this.newGuest.dienThoai = this.phoneSearch;

      try {
        const response = await this.$store.dispatch(
          'contacts/searchCrmContact',
          this.phoneSearch
        );
        if (response && response.data) {
          this.searchResults = response.data;
        }
        this.searchCompleted = true;
      } catch (error) {
        this.showAlert(this.$t('CRM_CONFIGS.LINK.SEARCH_ERROR'));
      } finally {
        this.isSearching = false;
      }
    },
    async linkContact(guest) {
      this.isLinking = true;
      this.selectedGuestId = guest.id;

      try {
        // First check if this CRM contact is already linked
        const checkResponse = await this.$store.dispatch(
          'contacts/checkCrmLink',
          guest.maKhach
        );

        if (checkResponse && checkResponse.linked) {
          if (checkResponse.contact.id === this.contact.id) {
            this.showAlert(this.$t('CRM_CONFIGS.LINK.ALREADY_LINKED_HERE'));
          } else {
            this.showAlert(
              this.$t('CRM_CONFIGS.LINK.ALREADY_LINKED_OTHER', {
                name: checkResponse.contact.name,
              })
            );
          }
          return;
        }

        const customAttributes = {
          ...this.contact.custom_attributes,
          crm_contact: {
            id: guest.id,
            maKhach: guest.maKhach,
            hoTen: guest.hoTen,
            dienThoai: guest.dienThoai,
          },
        };

        await this.$store.dispatch('contacts/update', {
          id: this.contact.id,
          custom_attributes: customAttributes,
        });

        this.showAlert(this.$t('CRM_CONFIGS.LINK.LINK_SUCCESS'));
        this.onClose();
      } catch (error) {
        this.showAlert(this.$t('CRM_CONFIGS.LINK.LINK_ERROR'));
      } finally {
        this.isLinking = false;
        this.selectedGuestId = null;
      }
    },
    async submitCreateGuest() {
      this.$v.$touch();
      if (this.$v.$invalid) return;

      this.isCreating = true;

      try {
        const response = await this.$store.dispatch(
          'contacts/createCrmGuest',
          this.newGuest
        );

        if (response && response.success === '1') {
          this.showAlert(this.$t('CRM_CONFIGS.CREATE_GUEST.SUCCESS'));

          // Sau khi tạo xong, tự động gọi lại hàm search CRM để lấy đúng record đó về
          await this.searchCrm();

          // Hàm search sẽ gán record mới vào searchResults. Lấy record đó ra.
          if (this.searchResults && this.searchResults.length > 0) {
            const newlyCreatedGuest = this.searchResults[0];
            // Tự động kích hoạt liên kết!
            await this.linkContact(newlyCreatedGuest);
          } else {
            this.onClose();
          }
        } else {
          this.showAlert(this.$t('CRM_CONFIGS.CREATE_GUEST.ERROR'));
        }
      } catch (error) {
        this.showAlert(this.$t('CRM_CONFIGS.CREATE_GUEST.ERROR'));
      } finally {
        this.isCreating = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-container {
  min-width: 620px;
}

input {
  border-radius: 8px;
  @apply bg-slate-25 dark:bg-slate-900;
}

select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1rem;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
